// FarmDataForm.jsx
import React, { useState } from "react";
import "./FarmDataForm.css";
import { LOCATIONS } from "../api/programs";
import useLang from "../hooks/useLang";

function FarmDataForm({ profile, onSubmit }) {
  const { dict, t, lang } = useLang();
  const [formData, setFormData] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("");
  
  // Progress calculation
  const calculateProgress = () => {
    let completed = 0;
    const totalSteps = 4;
    
    if (formData.country) completed++;
    if (formData.region) completed++;
    if (formData.farmType) completed++;
    if (formData.goal) completed++;
    
    return (completed / totalSteps) * 100;
  };

  // Multilingual progress texts
  const progressTexts = {
    de: { progress: "Fortschritt", complete: "Vollständig" },
    es: { progress: "Progreso", complete: "Completo" },
    it: { progress: "Progresso", complete: "Completo" }
  };
  
  const currentProgressText = progressTexts[lang] || progressTexts.de;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Wenn Land geändert wird, Region zurücksetzen
    if (name === "country") {
      setSelectedCountry(value);
      setFormData((prev) => ({ ...prev, country: value, region: "" }));
    }
  };

  // Übersetzungsschicht: UI-Werte zu Backend-deutschen Werten
  const translateToBackend = (formData) => {
    // Mapping für farmType (Betriebsart)
    const farmTypeMapping = {
      // Deutsch (bleibt gleich)
      'Milchvieh': 'Milchvieh',
      'Ackerbau': 'Ackerbau', 
      'Gemischt': 'Gemischt',
      'Bio': 'Bio',
      'Viehzucht': 'Viehzucht',
      'Sonstiges': 'Sonstiges',
      // Spanisch zu Deutsch
      'Ganado lechero': 'Milchvieh',
      'Cultivos extensivos': 'Ackerbau',
      'Mixto': 'Gemischt',
      'Orgánico': 'Bio',
      'Ganadería': 'Viehzucht',
      'Otros': 'Sonstiges',
      // Italienisch zu Deutsch
      'Latticini': 'Milchvieh',
      'Coltivazioni estensive': 'Ackerbau',
      'Misto': 'Gemischt',
      'Biologico': 'Bio',
      'Allevamento': 'Viehzucht',
      'Altro': 'Sonstiges',
      'Viticoltura': 'Weinbau'
    };

    // Mapping für goal (Ziel)
    const goalMapping = {
      // Deutsch (bleibt gleich)
      'Digitalisierung': 'Digitalisierung',
      'Nachhaltigkeit': 'Nachhaltigkeit',
      'Investieren': 'Investieren',
      'Wachstum': 'Wachstum',
      // Spanisch zu Deutsch
      'Digitalizar': 'Digitalisierung',
      'Ser más sostenible': 'Nachhaltigkeit',
      'Invertir y crecer': 'Investieren',
      'Crecer': 'Wachstum',
      // Italienisch zu Deutsch
      'Digitalizzare': 'Digitalisierung',
      'Essere più sostenibili': 'Nachhaltigkeit',
      'Investire e crescere': 'Investieren',
      'Crescere': 'Wachstum'
    };

    return {
      ...formData,
      farmType: farmTypeMapping[formData.farmType] || formData.farmType,
      goal: goalMapping[formData.goal] || formData.goal
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const translatedData = translateToBackend(formData);
    console.log('Form submitting data:', { profile, ...translatedData });
    onSubmit({ profile, ...translatedData });
  };

  return (
    <div className="container">
      <div className="max-w-2xl mx-auto">
        {/* Header mit Emoji - Mobile optimiert */}
        <div className="text-center mb-6 sm:mb-8 px-4 sm:px-0">
          <div className="text-4xl sm:text-6xl mb-4">🌾</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 leading-tight">{t('projectTitle')}</h2>
          <p className="text-gray-600 text-sm sm:text-base">Erzählen Sie uns von Ihrem Betrieb</p>
          
          {/* Progress Bar */}
          <div className="mt-6 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                {calculateProgress() === 100 ? currentProgressText.complete : currentProgressText.progress}
              </span>
              <span className="text-sm font-medium text-green-600">{Math.round(calculateProgress())}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ease-out ${
                  calculateProgress() === 100 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg' 
                    : 'bg-gradient-to-r from-green-400 to-green-600'
                }`}
                style={{ width: `${calculateProgress()}%` }}
              >
                {calculateProgress() === 100 && (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-white text-xs font-bold animate-pulse">✓</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-4">
            <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
              ⏱️ Dauert nur 3 Minuten
            </div>
          </div>
        </div>
        
        <form className="bg-white rounded-2xl shadow-xl p-8 space-y-6" onSubmit={handleSubmit}>
        
        {/* Frage 1 - Land */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
            <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              🌍 {t('q1')}
            </label>
          </div>
          <select
            name="country"
            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-lg"
            onChange={handleChange}
            value={formData.country || ""}
            required
          >
            <option value="" disabled>
              {t('country')} {t('select')}
            </option>
            {Object.entries(LOCATIONS).map(([countryCode, countryData]) => (
              <option key={countryCode} value={countryCode}>
                {typeof countryData.label === 'object' ? countryData.label[lang] || countryData.label.de : countryData.label}
              </option>
            ))}
          </select>
        </div>

        {/* Region auswählen (nur wenn Land ausgewählt) */}
        {selectedCountry && (
          <div className="space-y-3 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">📍</div>
              <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                🗺️ {t('region')}
              </label>
            </div>
            <select
              name="region"
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-lg"
              onChange={handleChange}
              value={formData.region || ""}
              required
            >
              <option value="" disabled>
                {t('region')} {t('select')}
              </option>
              {LOCATIONS[selectedCountry]?.regions.map((region) => (
                <option key={region.value} value={region.value}>
                  {typeof region.label === 'object' ? region.label[lang] || region.label.de : region.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Frage 2 - Betriebsart */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
            <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              🚜 {t('q2')}
            </label>
          </div>
          <select
            name="farmType"
            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-lg"
            onChange={handleChange}
            value={formData.farmType || ""}
            required
          >
            <option value="" disabled>
              {t('select')}
            </option>
            {dict.q2Options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Frage 3 - Ziel */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
            <label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              🎯 {t('q3')}
            </label>
          </div>
          <select
            name="goal"
            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors text-lg"
            onChange={handleChange}
            value={formData.goal || ""}
            required
          >
            <option value="" disabled>
              {t('select')}
            </option>
            {dict.q3Options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

          {/* Submit Button - Aufgehübscht */}
          <div className="pt-6 border-t border-gray-100">
            <button 
              type="submit" 
              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 px-8 rounded-xl text-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3"
            >
              <span className="text-2xl">🚀</span>
              {t('submitButton')}
              <span className="text-lg">→</span>
            </button>
            
            {/* Vertrauen unter Button */}
            <div className="text-center mt-4 space-y-2">
              <div className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <span>🔒</span>
                Ihre Daten sind sicher und werden verschlüsselt übertragen
              </div>
              <div className="text-sm text-green-600 font-medium">
                ✓ Nur 9,90€ • ✓ Sofortige Ergebnisse • ✓ 30 Tage Garantie
              </div>
            </div>
          </div>
        </form>
        
        {/* Progress Indicator */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 text-sm text-gray-600">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Schritt 1 von 2</span>
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FarmDataForm;